/**
 * FLX bespoke landing — chrome (fixed nav + mobile menu + footer) and the
 * `.flx-root` wrapper that scopes `flx.css`.
 *
 * Markup ported from the client's live site; the class names are theirs, so the
 * ported stylesheet drives it directly. Deliberately does NOT use the shared
 * member `<Header/>`: this public marketing page owns its own chrome.
 *
 * TWO ADDITIONS the live site has no equivalent of, because it does not sell
 * yet: a cart button and an account link. `renderTenantPage` folds `/storefront`
 * back into this page, so the account link is the only route from the public site
 * to `/login` or the portal.
 *
 * The landing does NOT own the checkout. The cart button and every Add to Cart
 * hand off to the SHARED `/cart` → `/checkout` pages, the same ones every other
 * tenant uses.
 *
 * The live site toggles `body.menu-open` for the fullscreen mobile menu; scoped,
 * that rule is `.flx-root.menu-open`, so the state lives here rather than in the
 * nav.
 */
/* eslint-disable react-refresh/only-export-components --
 * `scrollToAnchor` is a handler factory shared by the landing sections and lives
 * with the nav it scrolls; only fast-refresh granularity is affected. */
import { type ReactNode, type MouseEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../../lib/auth-store';
import { useCartStore, cartCount } from '../../../storefront/lib/cart-store';
import { useSyncCartPricesOnLogin } from '../../../storefront/lib/hooks';
import { NAV, FOOTER, FOOTER_BLURB, FOOTER_COMPANY, FOOTER_LEGAL, BRAND_TAG, IMG } from './tokens';
import './flx.css';

/** Smooth-scroll to an in-page `#anchor`; ignore route hrefs (`/…`) + bare `#`. */
export const scrollToAnchor = (href: string) => (e: MouseEvent) => {
  if (!href.startsWith('#') || href === '#') return;
  e.preventDefault();
  const id = href.slice(1);
  document.getElementById(id === 'top' ? 'hero' : id)?.scrollIntoView({ behavior: 'smooth' });
};

/** The FLX lockup: the client's logo inside the live site's white `.logo-chip`. */
export function FlxWordmark({ height = 32, pad = '4px 9px' }: { height?: number; pad?: string }) {
  return (
    <span className="logo-chip" style={{ height, padding: pad }}>
      <img src={IMG.logo} alt="FLX" />
    </span>
  );
}

function FlxNav({
  menuOpen,
  setMenuOpen,
}: {
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
}) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(-document.documentElement.getBoundingClientRect().top > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, setMenuOpen]);

  return (
    <>
      <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="#hero" className="nav-logo" onClick={scrollToAnchor('#hero')}>
            <FlxWordmark />
            <span className="tag">{BRAND_TAG}</span>
          </a>
          <div className="nav-links">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={scrollToAnchor(n.href)}>
                {n.label}
              </a>
            ))}
            <AccountLink />
          </div>
          <CartButton />
          <a
            href="#early-access"
            className="btn btn--primary nav-cta"
            onClick={scrollToAnchor('#early-access')}
          >
            Early Access <span className="arrow">→</span>
          </a>
          <button
            type="button"
            className={`nav-burger${menuOpen ? ' is-open' : ''}`}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <div className="mobile-menu">
        <nav className="mm-links" aria-label="Mobile">
          {NAV.map((n, i) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => {
                scrollToAnchor(n.href)(e);
                setMenuOpen(false);
              }}
            >
              <span className="mm-no">{String(i + 1).padStart(2, '0')}</span>
              <span className="mm-label">{n.label}</span>
            </a>
          ))}
        </nav>
      </div>
    </>
  );
}

/**
 * The way into the member portal.
 *
 * This page owns its chrome instead of rendering the shared `<Header/>`, so it
 * inherits none of that header's account links — and once `/storefront` folded
 * into this page there was no route from the public site to `/login` or the
 * dashboard at all. Signed-in members get their portal; everyone else gets
 * Log In. (Sign-up deliberately isn't here: FLX is sponsor-driven, so joining
 * runs through a kit purchase or an invite link, not a bare signup button.)
 */
function AccountLink() {
  const authed = !!useAuthStore((s) => s.token);
  return <Link to={authed ? '/dashboard' : '/login'}>{authed ? 'My Account' : 'Log In'}</Link>;
}

/** Cart affordance + count, linking to the SHARED cart page. */
function CartButton() {
  const count = useCartStore((s) => cartCount(s.lines));
  if (count === 0) return null;
  return (
    <Link
      to="/cart"
      className="flx-cart-btn"
      aria-label={`View cart (${count} ${count === 1 ? 'item' : 'items'})`}
    >
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M3 5h10l-1 9H4L3 5Z" stroke="currentColor" strokeWidth="1.2" />
        <path d="M6 5V4a2 2 0 0 1 4 0v1" stroke="currentColor" strokeWidth="1.2" />
      </svg>
      <span className="flx-cart-count">{count}</span>
    </Link>
  );
}

function FlxFooter() {
  return (
    <footer>
      <div className="container">
        <div className="footer-top">
          <div className="footer-brand">
            <FlxWordmark height={36} pad="5px 10px" />
            <p>{FOOTER_BLURB}</p>
            <p style={{ fontSize: 12, opacity: 0.4, marginTop: 16, lineHeight: 1.6 }}>
              {FOOTER_COMPANY.name}
              <br />
              {FOOTER_COMPANY.address}
            </p>
          </div>
          {FOOTER.map((col) => (
            <div className="footer-col" key={col.h}>
              <h5>{col.h}</h5>
              <ul>
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} onClick={scrollToAnchor(l.href)}>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          {FOOTER_LEGAL.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      </div>
    </footer>
  );
}

export function FlxShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  // This page displays prices, so it owes the buyer the price they'll be charged:
  // lines snapshotted at SRP while logged out are re-priced the moment they
  // authenticate (in the checkout overlay, another tab, or a restored session).
  useSyncCartPricesOnLogin();
  return (
    <div id="top" className={`flx-root${menuOpen ? ' menu-open' : ''}`}>
      <FlxNav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      {children}
      <FlxFooter />
    </div>
  );
}
